import type {
  ChangeCommitmentAndStateCommandData,
  CommandSimulateData,
  CommitmentSimulationData,
  CoverEdit,
  PolicyEdit,
} from '@festina/life-general-types/life'
import { dateUtils, notEmpty, objectUtils, typeUtils } from '@festina-life/core'
import { Alert } from '@festina-life/ui/feedback'
import type { SubmitStatus } from '@festina-life/ui/formikForms'
import { useTranslate } from '@festina-life/ui/hooks'
import { SimpleModal } from '@festina-life/ui/structure'
import dayjs from 'dayjs'
import { useCallback, useMemo, useState } from 'react'

import { useScenarioContext } from '../../context/scenarioContext'
import { useTime } from '../../hooks/useTime'
import { useHook } from '../../lenteHooks'
import { ErrorMessage } from '../errorFallback/ErrorMessage'
import { DiffObjectView } from './DiffObjectView'

type Props = {
  initialValues: ChangeCommitmentAndStateCommandData
  command: ChangeCommitmentAndStateCommandData
  onSubmit: (values: ChangeCommitmentAndStateCommandData) => Promise<unknown>
  onStatusChanged: (status: SubmitStatus) => void
  onClose: () => void
}

export const ChangeCommitmentReviewModal = ({
  initialValues,
  command,
  onClose,
  onStatusChanged,
  onSubmit,
}: Props) => {
  const translate = useTranslate()

  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>()

  const { tenantId, ownerId } = useHook('routing.general.useEntityParams')()
  const { scenarioIdForRequestHeader } = useScenarioContext()

  const { data: time } = useTime()
  const nextMonth = dayjs(time).add(1, 'month')

  const cleanedCommand = useMemo(() => {
    const { commitment, commitmentState, policyEdits, ...values } = command

    const { policies, ...cleanCommitment } =
      commitment ??
      ({
        _type: 'Commitment',
      } satisfies ChangeCommitmentAndStateCommandData['commitment'])

    const {
      policyStates,
      applyDate, // not used by backend but still checked
      calculationDate, // not used by backend but still checked
      ...cleanCommitmentState
    } =
      commitmentState ??
      ({
        _type: 'CommitmentState',
      } satisfies ChangeCommitmentAndStateCommandData['commitmentState'])

    const cleanPolicyEdits = policyEdits
      ?.filter(notEmpty)
      .map(({ policy, policyState, ...rest }) => {
        const { covers, policyEmployment, ...cleanPolicy } =
          policy ??
          ({
            _type: 'Policy',
          } satisfies PolicyEdit['policy'])

        const { coverStates, ...cleanPolicyState } =
          policyState ??
          ({
            _type: 'PolicyState',
          } satisfies PolicyEdit['policyState'])

        return {
          ...rest,
          policy: policy && cleanPolicy,
          policyState: policyState && cleanPolicyState,
        } satisfies PolicyEdit
      })

    return {
      ...values,
      commitment: commitment && cleanCommitment,
      commitmentState: commitmentState && cleanCommitmentState,
      policyEdits: policyEdits && cleanPolicyEdits,
      _type: 'ChangeCommitmentAndStateCommandData',
    } satisfies ChangeCommitmentAndStateCommandData
  }, [command])

  const initialValuesWithAmends = useMemo(
    () => ({
      ...initialValues,
      policyEdits: command.policyEdits?.map((policyEdit): PolicyEdit | null => {
        switch (policyEdit?.editType) {
          case 'create':
            // A creation means there was previously nothing.
            return null
          case 'update':
          case 'delete': {
            const policyKey =
              policyEdit.editType === 'update'
                ? policyEdit.policy?.policyKey
                : policyEdit.policyKey

            return {
              _type: 'PolicyEdit',
              // Lack of `editType` is intentional, as this is not a real edit,
              // and is just meant to be a visual aid.
              policyKey,
              // Use the original, unmodified state as the previous initial values.
              policyState: command.commitmentState?.policyStates?.find(
                (policy) => policy?.policyKey === policyKey,
              ),
              policy: command.commitment?.policies?.find(
                (policy) => policy?.policyKey === policyKey,
              ),
            }
          }
          case undefined:
          case null:
          default:
            return null
        }
      }),
      coverEdits: command.coverEdits?.map((coverEdit): CoverEdit | null => {
        switch (coverEdit?.editType) {
          case 'create':
            // A creation means there was previously nothing.
            return null
          case 'update':
          case 'delete': {
            const coverKey =
              coverEdit.editType === 'update'
                ? coverEdit.cover?.coverKey
                : coverEdit.coverKey

            return {
              _type: 'CoverEdit',
              // Lack of `editType` is intentional, as this is not a real edit,
              // and is just meant to be a visual aid.
              policyKey: coverEdit?.policyKey,
              // Use the original, unmodified state as the previous initial values.
              coverState: command.commitmentState?.policyStates
                ?.find((policy) => policy?.policyKey === coverEdit?.policyKey)
                ?.coverStates?.find((cover) => cover?.coverKey === coverKey),
              cover: command.commitment?.policies
                ?.find((policy) => policy?.policyKey === coverEdit?.policyKey)
                ?.covers?.find((cover) => cover?.coverKey === coverKey),
            }
          }
          case undefined:
          case null:
          default:
            return null
        }
      }),
    }),
    [command, initialValues],
  )

  const diffedCommand = useMemo(
    () => objectUtils.diffObjects(initialValuesWithAmends, command),
    [command, initialValuesWithAmends],
  )

  const { isLoading: isLoadingSimulation, error: errorSimulation } = useHook(
    'fetchers.life.useFetchCommitmentVersionSimulation',
  )({
    tenantId,
    ownerId,
    scenarioId: scenarioIdForRequestHeader,
    requestBody: {
      _type: 'CommitmentSimulationData',
      commands: [
        {
          _type: 'CommandSimulateData',
          commandData: cleanedCommand,
          commandType: 'changeCommitmentAndState',
        } satisfies CommandSimulateData,
      ],
      simulateTo: dateUtils.dateAsServerStr(nextMonth.toDate()),
    } as CommitmentSimulationData,
  })

  const handleStatus = useCallback(
    (status: SubmitStatus) => {
      setSubmitStatus(status)
      onStatusChanged(status)
    },
    [onStatusChanged],
  )

  const handleSubmit = useCallback(async () => {
    handleStatus('submitting')

    try {
      await onSubmit(cleanedCommand)
      handleStatus('success')
      onClose()
    } catch (e) {
      handleStatus(typeUtils.isError(e) ? e : new Error(`${e}`))
    }
  }, [cleanedCommand, onClose, handleStatus, onSubmit])

  return (
    <SimpleModal
      isOpen
      title={translate('changeCommitmentModalButton.confirm.title')}
      acceptConfiguration={{
        onAccept: handleSubmit,
        loading: isLoadingSimulation || submitStatus === 'submitting',
      }}
      closeConfiguration={{
        onClose,
      }}
    >
      {translate('changeCommitmentModalButton.confirm.body')}

      <DiffObjectView<ChangeCommitmentAndStateCommandData>
        diff={diffedCommand}
      />

      {errorSimulation ? (
        <ErrorMessage
          error={errorSimulation}
          title={translate(
            'changeCommitmentModalButton.confirm.simulationFailure',
          )}
        />
      ) : (
        <Alert severity={isLoadingSimulation ? 'info' : 'success'}>
          {isLoadingSimulation
            ? translate('changeCommitmentModalButton.confirm.simulationPending')
            : translate(
                'changeCommitmentModalButton.confirm.simulationSuccess',
              )}
        </Alert>
      )}
      {typeUtils.isError(submitStatus) && <ErrorMessage error={submitStatus} />}
    </SimpleModal>
  )
}
